// Phase D — Customer-side delivery progress timeline.
//
// Vertical timeline showing each customer-visible stage: registered →
// in_progress → offered → won → executed → delivered. Each node renders as
// completed / current (pulsing) / pending. Server-side filtering — this
// page never receives margin/cost data.
import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Loader2, XCircle, Download, MapPin, Package, ShieldCheck, AlertOctagon } from "lucide-react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useT } from "../../lib/i18n";
import { FileClaimSection } from "./PortalClaims";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const useTimeline = (rid, locale) =>
  useQuery({
    queryKey: ["portal-timeline", rid, locale],
    queryFn: () =>
      axios
        .get(`${API}/logistics-requests/${rid}/timeline`, { params: { locale } })
        .then((r) => r.data),
    enabled: !!rid,
  });

export default function PortalTimeline() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const { locale, t } = useT();
  const { data, isLoading, error } = useTimeline(rid, locale);
  const qc = useQueryClient();
  // J.5c — Accept-offer state.
  const [accepting, setAccepting] = useState(false);
  const [acceptResult, setAcceptResult] = useState(null);

  const onAccept = async () => {
    if (!window.confirm(t("portal.accept.confirm"))) return;
    setAccepting(true);
    try {
      const r = await axios.post(`${API}/portal/freight-deals/${rid}/accept`, {});
      setAcceptResult(r.data);
      if (r.data?.status === "accepted") {
        toast.success(t("portal.accept.btn"));
      } else if (r.data?.status === "pending_internal_approval") {
        toast.message(t("portal.accept.pending_notice"), {
          description: `${Number(r.data.threshold).toLocaleString()} ≥ ${Number(r.data.amount).toLocaleString()}`,
        });
      }
      qc.invalidateQueries({ queryKey: ["portal-timeline", rid] });
    } catch (e) {
      const msg = e.response?.data?.detail?.message || e.response?.data?.detail || "Accept failed";
      toast.error(typeof msg === "string" ? msg : "Accept failed");
    } finally {
      setAccepting(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const variant = data?.summary?.id && (data?.current_stage_customer === "executed" || data?.current_stage_customer === "delivered") ? "billing" : "offer";
      const url = `${API}/logistics-requests/${rid}/customer-pdf?lang=${locale}&variant=${variant}`;
      const r = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `SITROOM_${variant}_${rid}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (_) {
      toast.error("PDF download failed");
    }
  };

  if (isLoading) return <div style={mono(11, "var(--text-dim, #9aa1b3)")}>{t("portal.list.loading")}</div>;
  if (error || !data) {
    return (
      <div data-testid="portal-timeline-error" style={mono(12, "#ff8a8a")}>
        Could not load this request.
        <button onClick={() => navigate("/portal/my-requests")} style={{ marginLeft: 8, ...mono(10, "#79b5f0") }}>
          Back
        </button>
      </div>
    );
  }

  const { summary, items, terminal_state } = data;

  return (
    <div data-testid="portal-timeline-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Link
            to="/portal/my-requests"
            className="inline-flex items-center gap-1 px-2 py-1 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", ...mono(11, "var(--text-dim, #c5cad8)"), textDecoration: "none" }}
            data-testid="portal-timeline-back"
          >
            <ArrowLeft size={13} /> {t("portal.chat.back")}
          </Link>
          <div>
            <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 22, color: "var(--text, #f0f2f8)" }}>
              {summary.deal_name || summary.id}
            </h1>
            <div style={mono(10, "var(--text-dim, #6b7388)")}>{summary.id}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={downloadPdf}
          className="inline-flex items-center gap-2 px-3 py-2 rounded"
          style={{ background: "#7e3aa8", color: "#fff", border: "none", ...mono(10, "#fff", 700, "0.8px"), textTransform: "uppercase", cursor: "pointer" }}
          data-testid="portal-timeline-pdf"
        >
          <Download size={12} /> PDF
        </button>
        {summary.deal_stage === "offered" && !summary.customer_accept_pending && (
          <button
            type="button"
            onClick={onAccept}
            disabled={accepting}
            className="inline-flex items-center gap-2 px-3 py-2 rounded"
            style={{ background: "#0a8754", color: "#fff", border: "none", ...mono(10, "#fff", 700, "0.8px"), textTransform: "uppercase", cursor: accepting ? "wait" : "pointer", opacity: accepting ? 0.7 : 1 }}
            data-testid="portal-accept-offer-btn"
          >
            {accepting ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
            {accepting ? t("portal.accept.btn_loading") : t("portal.accept.btn")}
          </button>
        )}
      </div>

      {summary.deal_stage === "offered" && (summary.customer_accept_pending || acceptResult?.status === "pending_internal_approval") && (
        <div
          className="rounded p-3 mb-4 flex items-start gap-2"
          style={{ background: "rgba(230,167,0,0.08)", border: "1px solid rgba(230,167,0,0.40)" }}
          data-testid="portal-accept-pending-notice"
        >
          <AlertOctagon size={14} color="#e6a700" className="mt-0.5" />
          <div style={mono(11, "#e6a700", 600)}>
            {t("portal.accept.pending_notice")}{" "}
            {acceptResult?.threshold != null && acceptResult?.amount != null
              && `(${Number(acceptResult.amount).toLocaleString()} > ${Number(acceptResult.threshold).toLocaleString()})`}
          </div>
        </div>
      )}

      {/* Route + cargo summary */}
      <section
        className="rounded p-4 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
        data-testid="portal-timeline-summary"
      >
        <div>
          <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1.2px"), textTransform: "uppercase", marginBottom: 4 }}>
            <MapPin size={10} className="inline mr-1" />{t("portal.form.section_pickup")}
          </div>
          <div style={mono(13, "var(--text, #f0f2f8)", 500)}>
            {[summary.pickup_city, summary.pickup_country].filter(Boolean).join(", ") || "—"}
          </div>
        </div>
        <div>
          <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1.2px"), textTransform: "uppercase", marginBottom: 4 }}>
            <MapPin size={10} className="inline mr-1" />{t("portal.form.section_dropoff")}
          </div>
          <div style={mono(13, "var(--text, #f0f2f8)", 500)}>
            {[summary.dropoff_city, summary.dropoff_country].filter(Boolean).join(", ") || "—"}
          </div>
        </div>
        <div>
          <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1.2px"), textTransform: "uppercase", marginBottom: 4 }}>
            <Package size={10} className="inline mr-1" />{t("portal.form.section_cargo")}
          </div>
          <div style={mono(13, "var(--text, #f0f2f8)", 500)}>
            {summary.aru_megnevezese || "—"}
            {summary.suly_kg ? ` · ${summary.suly_kg} kg` : ""}
          </div>
        </div>
      </section>

      {/* Terminal state banner */}
      {terminal_state && (
        <div
          className="rounded p-3 mb-4 inline-flex items-center gap-2"
          style={{ background: "rgba(226,75,74,0.12)", color: "#ff8a8a", border: "1px solid rgba(226,75,74,0.45)", ...mono(11, "#ff8a8a", 700, "0.6px"), textTransform: "uppercase" }}
          data-testid={`portal-timeline-terminal-${terminal_state}`}
        >
          <XCircle size={13} />
          {terminal_state === "cancelled" ? "Request cancelled" : "Offer not won"}
        </div>
      )}

      {/* Vertical timeline */}
      <section
        className="rounded p-5"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(155,89,182,0.25)" }}
        data-testid="portal-timeline-stages"
      >
        <div className="relative" style={{ paddingLeft: 30 }}>
          {/* vertical line */}
          <div
            style={{
              position: "absolute",
              left: 9,
              top: 6,
              bottom: 6,
              width: 2,
              background: "linear-gradient(180deg, rgba(155,89,182,0.50) 0%, rgba(155,89,182,0.10) 100%)",
            }}
            aria-hidden="true"
          />
          {items.map((it) => {
            const Icon = it.completed ? CheckCircle2 : (it.current ? Loader2 : Circle);
            const color = it.completed ? "#4ed084" : (it.current ? "#c89adb" : "#5a6275");
            return (
              <div
                key={it.key}
                className="relative mb-5 last:mb-0"
                data-testid={`portal-timeline-stage-${it.key}`}
                data-state={it.completed ? "completed" : (it.current ? "current" : "pending")}
              >
                <div
                  style={{
                    position: "absolute",
                    left: -30,
                    top: 0,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: "#0d0f18",
                    border: `2px solid ${color}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon size={11} color={color} className={it.current ? "animate-spin" : ""} />
                </div>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div
                    style={{
                      ...mono(13, color, it.current || it.completed ? 700 : 500, "0.4px"),
                      textTransform: "uppercase",
                    }}
                  >
                    {it.label}
                    {it.current && (
                      <span style={{ marginLeft: 8, ...mono(9, "#c89adb", 700, "1.2px"), background: "rgba(155,89,182,0.18)", padding: "2px 6px", borderRadius: 4, textTransform: "uppercase" }}>
                        Now
                      </span>
                    )}
                  </div>
                  <div style={mono(10, "var(--text-dim, #9aa1b3)")}>
                    {it.at ? String(it.at).slice(0, 16).replace("T", " ") : (it.pending ? "—" : "")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Phase E — File a claim — only shown when executed && within 5-day SLA */}
      <FileClaimSection logisticsRequestId={rid} executedAt={data.executed_at} />
    </div>
  );
}
